import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import styles from './mainNavBar.module.scss'
import { DesktopNavigationProps } from './types'
import { getLocalizedPath, isActivePath } from './utils'
import AccountToggleDesktop from './AccountToggleDesktop'
import UserApi from 'api/rest/user'
import { ReactComponent as ArrowDownIcon } from '../../images/icons/icn_arrow_down.svg'
import { SignIn } from '../navbar/links'
import { RegisterBtn } from '../navbar/buttons'
import { POST_SCORE_HREF, WATCHLISTS_HREF, MY_ACCOUNT_HREF } from 'constants/global'
import { getOwnProfileUrl } from 'utils'

const DesktopNavigation: React.FC<DesktopNavigationProps> = ({
  navItems,
  activeDropdown,
  toggleDropdown,
  dropdownRefs,
  isLoggedIn,
  userProfile,
  canPostScore,
  hasPendingMatches,
}) => {
  const { t } = useTranslation(['strCommonUI'])

  const accountItems = useMemo(
    () => [
      {
        id: 'acc-my-profile',
        name: t('link.myProfile'),
        path: getOwnProfileUrl(),
        isProfileItem: true,
      },
      {
        id: 'acc-my-account',
        name: t('link.myAccount'),
        path: MY_ACCOUNT_HREF,
      },
      {
        id: 'acc-watchlists',
        name: t('link.myWatchlists'),
        path: WATCHLISTS_HREF,
      },
      {
        id: 'acc-sign-out',
        name: t('button.signOut'),
        path: '#',
        action: () => UserApi.signOut(),
      },
    ],
    [t]
  )

  return (
    <ul className={styles.navPrimary} data-testid='desktop-navigation'>
      {isLoggedIn && canPostScore && (
        <li className={classNames(styles.navItem, styles.hideOnMobile, styles.postScoreItem)}>
          <a href={POST_SCORE_HREF} className={styles.navLink}>
            {t('button.postAScore')} <span className={styles.navBadge}>{t('badge.new')}</span>
          </a>
        </li>
      )}
      {navItems.map(item => (
        <li
          key={item.id}
          className={classNames(styles.navItem, styles.hideOnMobile, {
            [styles.hasDropdown]: item.hasDropdown,
          })}
        >
          <a
            href={item.hasDropdown ? '#' : getLocalizedPath(item.path)}
            className={classNames(
              styles.navLink,
              {
                [styles.selected]: !item.hasDropdown && isActivePath(item.path),
              },
              {
                [styles.active]: activeDropdown === item.name,
              }
            )}
            onClick={
              item.hasDropdown
                ? e => {
                    e.preventDefault()
                    toggleDropdown(item.name)
                  }
                : item.action
                ? e => {
                    e.preventDefault()
                    item.action?.()
                  }
                : undefined
            }
            data-dropdown-toggle={item.hasDropdown ? item.name : undefined}
            aria-haspopup={item.hasDropdown ? 'true' : undefined}
            aria-expanded={item.hasDropdown ? activeDropdown === item.name : undefined}
          >
            <span className={styles.navLinkText}>{item.name}</span>
            {item.badge && <span className={styles.navBadge}>{item.badge}</span>}
            {item.hasDropdown && (
              <span className={styles.arrowContainer}>
                <ArrowDownIcon className={styles.arrowIcon} />
              </span>
            )}
          </a>

          {item.hasDropdown && item.dropdownItems && (
            <div
              ref={dropdownRefs[item.name] ?? undefined}
              className={classNames(styles.dropdown, {
                [styles.show]: activeDropdown === item.name,
              })}
              role='menu'
              aria-label={`${item.name} submenu`}
            >
              <ul>
                {item.dropdownItems.map((dItem, dIndex) => (
                  <li key={dItem.id ?? dIndex} role='none'>
                    <a
                      href={getLocalizedPath(dItem.path)}
                      role='menuitem'
                      onClick={() => toggleDropdown('')}
                    >
                      {dItem.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </li>
      ))}

      {isLoggedIn && navItems.length > 0 && (
        <li className={classNames(styles.dividerParent, styles.hideOnMobile)} aria-hidden='true'>
          <div className={styles.navDivider} aria-hidden='true'></div>
        </li>
      )}

      <li className={classNames(styles.navItem, styles.mainNavControls, styles.hideOnMobile)}>
        {isLoggedIn ? (
          <AccountToggleDesktop
            user={userProfile}
            items={accountItems}
            hasPendingMatches={hasPendingMatches}
          />
        ) : (
          <div className={styles.authButtons}>
            <div className={styles.authDivider} aria-hidden='true'></div>
            <SignIn />
            <RegisterBtn />
          </div>
        )}
      </li>
    </ul>
  )
}

export default DesktopNavigation
